import { ApolloQueryResult, OperationVariables, useLazyQuery, useMutation } from '@apollo/client'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { InitializedRecord } from '@orbit/records'
import {
  SavedReports,
  SavedReportsMenuActions,
} from 'components/SavedReports/SavedReports'
import type { SaveScheduledExportArgs } from 'components/SavedReports/SavedReportsDialogs'
import { GET_DISTRIBUTION_LISTS_QUERY } from 'components/pages/Groups/queries'
import { TimeRange } from 'components/DatePickerPopover/types'
import {
  PerformanceSavedReportConfigInput,
  SavedReport,
  ScheduledExport,
} from 'generated/graphql'
import { useCurrentUser } from 'lib/CurrentUserProvider'
import { omit } from 'lodash-es'
import { useCallback } from 'react'
import {
  CREATE_PERFORMANCE_SAVED_REPORT_MUTATION,
  DESTROY_PERFORMANCE_SAVED_REPORT_MUTATION,
  UPDATE_PERFORMANCE_SAVED_REPORT_MUTATION,
} from './data/mutations'
import { CREATE_SCHEDULED_EXPORT_MUTATION, DESTROY_SCHEDULED_EXPORT_MUTATION, UPDATE_SCHEDULED_EXPORT_MUTATION } from 'components/SavedReports/mutations'
import { GET_SCHEDULED_EXPORTS_QUERY } from 'components/SavedReports/queries'
import { currentSettingsMatchSelectedReport } from './PerformanceTable'
import { serializeSavedReportConfig } from './serialization'
import { TableSettings } from './TableSettings/types'
import { savedReportSchema, scheduledExportSchema } from 'components/SavedReports/yupSchemas'


export type SavedReportMenuProps = {
  anchorEl: HTMLElement | null
  onClose: () => void
  sidebarOpen: boolean
  selectedReport: SavedReport | null
  setSelectedReport: (report: SavedReport | null) => void
  tableSettings: TableSettings
  setTableSettings: (settings: TableSettings | ((settings: TableSettings) => TableSettings)) => void
  dateRange: TimeRange
  refetch: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<unknown>>
  setExportOpen: (isOpen: boolean) => void
  revertToSelectedSavedReport: () => void
}

type PerformanceSavedReportMenuProps = {
  anchorEl: HTMLElement | null
  onClose: () => void
  sidebarOpen: boolean
  selectedReport: SavedReport | null
  actions: SavedReportsMenuActions
}

function PerformanceSavedReportMenu({
  anchorEl,
  onClose,
  sidebarOpen,
  selectedReport,
  actions,
}: PerformanceSavedReportMenuProps) {
  return(
    <Menu
      id='saved-report-menu'
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: 0, horizontal: sidebarOpen ? 0 : 30 }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: { width: 240, textOverflow: 'ellipsis', overflowX: 'hidden', overflowY: 'auto', whiteSpace: 'nowrap' },
      }}
    >
      <MenuItem onClick={actions.openSaveNewReport} aria-label='Save as New Report'>Save as New Report</MenuItem>
      <MenuItem onClick={actions.openSaveReport} disabled={!selectedReport || !actions.savedReportHasChanges} aria-label='Save Report'>Save Report</MenuItem>
      <MenuItem onClick={actions.revertToSavedReport} disabled={!selectedReport || !actions.savedReportHasChanges}>Revert to Saved</MenuItem>
      <MenuItem onClick={actions.openExport}>Export</MenuItem>
      <MenuItem onClick={actions.openScheduledExports}>Scheduled Exports</MenuItem>
      <MenuItem onClick={actions.shareReport} disabled={!selectedReport}>Share</MenuItem>
      <MenuItem onClick={actions.openRenameReport} disabled={!selectedReport}>Rename</MenuItem>
      <MenuItem onClick={actions.openDeleteReport} disabled={!selectedReport}>Delete</MenuItem>
    </Menu>
  )
}

export function SavedReportMenu({
  anchorEl,
  onClose,
  sidebarOpen,
  selectedReport,
  setSelectedReport,
  tableSettings,
  setTableSettings,
  dateRange,
  refetch,
  setExportOpen,
  revertToSelectedSavedReport,
}: SavedReportMenuProps) {
  const { currentUser } = useCurrentUser()
  const [gqlCreateReport] = useMutation(CREATE_PERFORMANCE_SAVED_REPORT_MUTATION)
  const [gqlUpdateReport] = useMutation(UPDATE_PERFORMANCE_SAVED_REPORT_MUTATION)
  const [gqlDeleteReport] = useMutation(DESTROY_PERFORMANCE_SAVED_REPORT_MUTATION)
  const [gqlCreateScheduledExport] = useMutation(CREATE_SCHEDULED_EXPORT_MUTATION)
  const [gqlUpdateScheduledExport] = useMutation(UPDATE_SCHEDULED_EXPORT_MUTATION)
  const [gqlDeleteScheduledExport] = useMutation(DESTROY_SCHEDULED_EXPORT_MUTATION)
  const [gqlLoadScheduledExports] = useLazyQuery(GET_SCHEDULED_EXPORTS_QUERY)
  const [gqlLoadDistributionLists] = useLazyQuery(GET_DISTRIBUTION_LISTS_QUERY)

  const getReportConfig = useCallback(
    () => serializeSavedReportConfig(tableSettings, dateRange),
    [tableSettings, dateRange]
  )

  const createReport = useCallback(async ({
    name,
    reportConfig,
  }: {
    name: string
    reportConfig: PerformanceSavedReportConfigInput
  }) => {
    const response = await gqlCreateReport({
      variables: {
        name,
        reportConfig,
        reportKey: 'performance',
        userId: (currentUser as InitializedRecord).id,
      },
    })

    const savedReport = response.data?.mutationResult.savedReport
    if(!savedReport) {
      throw new Error('Saved report was not returned after creation')
    }

    void refetch()
    return savedReport
  }, [currentUser, gqlCreateReport, refetch])

  const updateReport = useCallback(async ({
    id,
    name,
    reportConfig,
  }: {
    id: string
    name?: string
    reportConfig?: PerformanceSavedReportConfigInput
  }) => {
    const response = await gqlUpdateReport({
      variables: { id, name, reportConfig },
    })

    const savedReport = response.data?.mutationResult.savedReport
    if(!savedReport) {
      throw new Error('Saved report was not returned after update')
    }

    void refetch()
    return savedReport
  }, [gqlUpdateReport, refetch])

  const deleteReport = useCallback(async (id: string) => {
    const response = await gqlDeleteReport({ variables: { id } })
    const savedReport = response.data?.mutationResult.savedReport

    if(!savedReport) {
      throw new Error('Saved report was not returned after deletion')
    }

    void refetch()
    return savedReport
  }, [gqlDeleteReport, refetch])

  const loadScheduledExports = useCallback(async () => {
    const response = await gqlLoadScheduledExports({
      variables: { userId: currentUser.id },
      fetchPolicy: 'network-only',
    })

    return [...(response.data?.scheduledExports ?? [])]
  }, [currentUser.id, gqlLoadScheduledExports])

  const loadDistributionLists = useCallback(async () => {
    const response = await gqlLoadDistributionLists({
      variables: { userId: currentUser.id },
    })

    return [...(response.data?.distributionLists ?? [])]
  }, [currentUser.id, gqlLoadDistributionLists])

  const saveScheduledExport = useCallback(async ({
    scheduledExport,
    report,
    isNew,
  }: SaveScheduledExportArgs) => {
    const variables = {
      dayOfMonth: scheduledExport.dayOfMonth,
      dayOfWeek: scheduledExport.dayOfWeek,
      filename: scheduledExport.filename,
      frequency: scheduledExport.frequency,
      recipients: scheduledExport.recipients ?? [],
      savedReportId: report.id,
      timeOfDay: scheduledExport.timeOfDay,
      weekCount: scheduledExport.weekCount,
      distributionListIds: scheduledExport.distributionLists?.map(list => list.id) ?? [],
    }

    if(isNew) {
      await gqlCreateScheduledExport({ variables })
      return
    }

    if(!scheduledExport.id) {
      throw new Error('Scheduled export id is required when updating')
    }

    await gqlUpdateScheduledExport({
      variables: { ...variables, id: scheduledExport.id },
    })
  }, [gqlCreateScheduledExport, gqlUpdateScheduledExport])

  const deleteScheduledExport = useCallback(async (id: string) => {
    const response = await gqlDeleteScheduledExport({ variables: { id } })
    const scheduledExport = response.data?.mutationResult.scheduledExport

    if(!scheduledExport) {
      throw new Error('Scheduled export was not returned after deletion')
    }

    return scheduledExport as ScheduledExport
  }, [gqlDeleteScheduledExport])

  const updateSelectedReportIdentity = useCallback((savedReport: SavedReport) => {
    setTableSettings(currentTableSettings => ({
      ...currentTableSettings,
      savedReportName: savedReport.name,
      savedReportId: savedReport.id,
    }))
    setSelectedReport(savedReport)
  }, [setSelectedReport, setTableSettings])

  const handleReportUpdated = useCallback((savedReport: SavedReport) => {
    setSelectedReport(savedReport)
  }, [setSelectedReport])

  const handleReportDeleted = useCallback((_savedReport: SavedReport) => {
    setTableSettings(currentTableSettings =>
      omit(currentTableSettings, ['savedReportId', 'savedReportName']) as TableSettings
    )
    setSelectedReport(null)
  }, [setSelectedReport, setTableSettings])

  const hasChanges = useCallback(
    () => !currentSettingsMatchSelectedReport(selectedReport, tableSettings, dateRange),
    [selectedReport, tableSettings, dateRange]
  )

  return(
    <SavedReports
      selectedReport={selectedReport}
      initialName={selectedReport?.name ?? ''}
      getReportConfig={getReportConfig}
      createReport={createReport}
      updateReport={updateReport}
      deleteReport={deleteReport}
      savedReportValidationSchema={savedReportSchema}
      scheduledExportValidationSchema={scheduledExportSchema}
      loadScheduledExports={loadScheduledExports}
      loadDistributionLists={loadDistributionLists}
      saveScheduledExport={saveScheduledExport}
      deleteScheduledExport={deleteScheduledExport}
      hasChanges={hasChanges}
      revertToSelectedSavedReport={revertToSelectedSavedReport}
      onReportCreated={updateSelectedReportIdentity}
      onReportUpdated={handleReportUpdated}
      onReportRenamed={updateSelectedReportIdentity}
      onReportDeleted={handleReportDeleted}
      onExport={() => setExportOpen(true)}
      onMenuClose={onClose}
      renderMenu={actions => (
        <PerformanceSavedReportMenu
          anchorEl={anchorEl}
          onClose={onClose}
          sidebarOpen={sidebarOpen}
          selectedReport={selectedReport}
          actions={actions}
        />
      )}
    />
  )
}
